import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { FormularioPerfil } from './FormularioPerfil';
import { actualizarSettings, leerSettings } from '@/db/schema';
import { datosDePerfilQueFaltan, NOMBRE_DATO_PERFIL } from '@/domain/perfil';
import { useFechaHoy } from '@/lib/useFechaHoy';

function lista(palabras: readonly string[]): string {
  if (palabras.length <= 1) return palabras.join('');
  return `${palabras.slice(0, -1).join(', ')} y ${palabras[palabras.length - 1]}`;
}

/**
 * Más → Perfil (Req. 4.2, 5.1, 5.7), con el estilo de Foco.
 *
 * Formulario completo (0.11.0): aquí se completa el perfil sin reiniciarlo.
 * Todo llega precargado y nada se borra; si falta algún dato, se dice cuál y
 * el botón ofrece completarlo.
 */
export function ProfilePage() {
  const hoy = useFechaHoy();
  const settings = useLiveQuery(() => leerSettings(), []);
  const [guardado, setGuardado] = useState(false);

  if (settings === undefined) return null;

  const faltan = datosDePerfilQueFaltan(settings);

  return (
    <div>
      <PageHeader
        compacto
        titulo="Tus datos"
        descripcion="Lo que la app lee para saludarte, mostrar tus unidades y calcular tu IMC y tu mantenimiento."
        volver={{ a: '/mas', texto: 'Volver', etiqueta: 'Volver a Más' }}
      />

      {guardado && (
        <div className="mb-5">
          <Banner tono="exito" titulo="Perfil guardado">
            Los cambios de unidades se ven en toda la app; lo que ya estaba guardado no cambia.
          </Banner>
        </div>
      )}

      {faltan.length > 0 && (
        <div className="mb-5">
          <Banner tono="info" titulo="Completar mi perfil">
            <p>
              {faltan.length === 1 ? 'Falta ' : 'Faltan '}
              {lista(faltan.map((d) => NOMBRE_DATO_PERFIL[d]))}.
            </p>
            <p>
              Todo es opcional y nada se supone. Completarlo no reinicia tu perfil ni borra ningún
              registro: tus metas y tus etapas siguen igual, y una propuesta de kcal o de macros se
              aplica solo si la aceptas.
            </p>
          </Banner>
        </div>
      )}

      <FormularioPerfil
        // La llave reinicia el formulario si los ajustes cambian por una restauración
        key={settings.perfilId}
        settings={settings}
        hoy={hoy}
        textoGuardar="Guardar cambios"
        onGuardar={async (cambios) => {
          setGuardado(false);
          await actualizarSettings(cambios);
          setGuardado(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      <p className="my-[17px] text-[12px] text-texto-suave">
        Cada uno conserva su perfil en su teléfono.
      </p>
    </div>
  );
}
